// Plan 08-02 — Apple ID Services JS SDK loader and sign-in helpers.
//
// Task 1 ships only the silent-reauth shim used by silent-refresh.ts; the
// full SDK loader + popup `signInWithApple()` lands in Task 2.
//
// SDK source: https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js
// Init shape (Pitfall 1): AppleID.auth.init({ clientId, scope: '', redirectURI,
//                                              usePopup: true, nonce })

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: {
          clientId: string
          scope: string
          redirectURI: string
          usePopup: boolean
          nonce: string
        }) => void
        signIn: () => Promise<{
          authorization: { id_token: string; code: string; state?: string }
          user?: { name?: unknown; email?: string }
        }>
      }
    }
  }
}

let sdkPromise: Promise<void> | null = null

export function loadAppleSDK(): Promise<void> {
  if (sdkPromise) return sdkPromise
  sdkPromise = new Promise<void>((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('loadAppleSDK requires a DOM'))
      return
    }
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-sdk="apple-id"]',
    )
    if (existing) {
      if (window.AppleID) {
        resolve()
      } else {
        existing.addEventListener('load', () => resolve(), { once: true })
        existing.addEventListener(
          'error',
          () => reject(new Error('Apple SDK failed to load')),
          { once: true },
        )
      }
      return
    }
    const script = document.createElement('script')
    script.src =
      'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
    script.async = true
    script.defer = true
    script.dataset.sdk = 'apple-id'
    script.addEventListener('load', () => resolve(), { once: true })
    script.addEventListener(
      'error',
      () => reject(new Error('Apple SDK failed to load')),
      { once: true },
    )
    document.head.appendChild(script)
  })
  return sdkPromise
}

/**
 * Trigger an interactive Apple sign-in popup. Returns the IdP-issued
 * id_token (JWT). Discards `result.user.*` per DEC-019.
 *
 * Plan 09-11 / CR-01: the caller MUST pass the same `nonce` that will be
 * sent in the exchange POST body. Apple bakes this nonce verbatim into the
 * JWT (nonce claim); the backend (apps/backend/src/auth/jwks.ts) raw-compares
 * the JWT nonce against the body nonce. Pre-fix this function generated its
 * own randomUUID internally — the caller's body nonce was DIFFERENT, so
 * Apple's strict nonce_supported=true path always 401'd in production.
 */
export async function signInWithApple(opts: {
  nonce: string
}): Promise<string> {
  await loadAppleSDK()
  if (!window.AppleID) {
    throw new Error('Apple SDK loaded but window.AppleID is undefined')
  }
  const { nonce } = opts
  const clientId = import.meta.env.VITE_APPLE_SERVICES_ID
  if (!clientId || clientId.startsWith('<')) {
    throw new Error('VITE_APPLE_SERVICES_ID not configured')
  }
  // Apple matches `redirectURI` against the Services ID's registered Return
  // URLs by EXACT string. We register `https://app.runegambit.com/auth/apple/callback`
  // (OPERATOR-RUNBOOK §17.5), so the bare origin Apple rejects with
  // `invalid_request: Invalid web redirect url`. With usePopup:true the URL is
  // never actually navigated to — the result comes via web_message postMessage
  // — but the string must still match a registered Return URL, so we
  // assemble it from `window.location.origin` plus the registered path.
  window.AppleID.auth.init({
    clientId,
    scope: '',
    redirectURI: `${window.location.origin}/auth/apple/callback`,
    usePopup: true,
    nonce,
  })
  const result = await window.AppleID.auth.signIn()
  // DEC-019 + Phase 2 invariant: discard result.user.* (name, email).
  return result.authorization.id_token
}

/**
 * Silent re-auth path. Apple JS exposes the same `signIn()` flow with the
 * popup suppressed when a recent sign-in is cached for the origin; in
 * pure-silent contexts (Safari ITP, third-party-cookie blocked, etc.) the
 * popup is required and we surface a rejection so the caller can route to
 * the splash login UI.
 *
 * Plan 09-11 / CR-01: caller-provided nonce threads through to the JWT.
 */
export async function silentReauthApple(opts: {
  nonce: string
}): Promise<string> {
  return signInWithApple(opts)
}
